import { PayloadAction } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import { call, put, takeLatest } from 'redux-saga/effects'

import history from '@/history'
import i18n from '@/i18n'
import { messageHandlerSet } from '@/redux/messageHandler/actions'
import * as AuthAPI from '@/services/auth'
import {
  userLoginFailed,
  userLoginRequest,
  userLoginSuccess,
  userLoginWithTokenRequest,
  userLogout
} from './actions'
import type {
  UserLoginRequestPayload,
  UserLoginWithTokenRequestPayload
} from './types'

type LoginWithCodeConfirmationResponse = { userLogin: string }

function * loginError (err: AxiosError) {
  if (err?.response?.status === 401) {
    yield put(
      messageHandlerSet({
        message: i18n.t('gui.notification.login_error_title'),
        type: 'error',
        description: i18n.t(
          'gui.notification.login_error_credentials_description'
        )
      })
    )
  } else {
    yield put(
      messageHandlerSet({
        message: i18n.t('gui.notification.login_error_title'),
        type: 'error',
        description: i18n.t('gui.notification.error_generic_description')
      })
    )
  }
  yield put(userLoginFailed())
  yield put(userLogout())
  history.replace('/login')
}

function * authLoginSaga ({ payload }: PayloadAction<UserLoginRequestPayload>) {
  const { username, password, role } = payload

  try {
    yield call(AuthAPI.login, {
      username,
      password,
      role
    })

    yield put(
      userLoginSuccess({
        userRoles: [role],
        username
      })
    )

    history.replace('/')
  } catch (err) {
    yield call(loginError, err as AxiosError)
  }
}

function * authLoginWithTokenSaga ({
  payload
}: PayloadAction<UserLoginWithTokenRequestPayload>) {
  const { token } = payload

  try {
    const result: LoginWithCodeConfirmationResponse = yield call(
      AuthAPI.loginWithCodeConfirmation,
      {
        token
      }
    )

    yield put(
      userLoginSuccess({
        userRoles: ['doctor'],
        username: result.userLogin
      })
    )

    history.replace('/')
  } catch (err) {
    yield put(
      messageHandlerSet({
        description: i18n.t('gui.notification.token_not_valid'),
        type: 'error'
      })
    )
    history.replace('/login')
  }
}

function * authSaga () {
  yield takeLatest(userLoginRequest.type, authLoginSaga)
  yield takeLatest(userLoginWithTokenRequest.type, authLoginWithTokenSaga)
}

export default authSaga
